import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import { extractCompanyJd, parseQuestionsFile, generateCompanyQuestions } from "../services/jobService";
import { downloadTemplate, McqQuestionForm, CodingQuestionForm, QuestionRow, UploadPreview } from "./QuestionForms";
import "./QuestionBankManager.css";

export const DIFFICULTY_OPTIONS = [
  { value: "easy", label: "Easy" },
  { value: "moderate", label: "Moderate" },
  { value: "difficult", label: "Difficult" },
  { value: "very_difficult", label: "Very Difficult" }
];

// {value, label} — value is what's sent to /generate-questions as the
// reference language; label is what recruiters see in the picker. Mirrors
// QuestionForms.jsx's LANGUAGES and company_question_bank.py's
// SUPPORTED_LANGUAGES.
export const CODING_LANGUAGES = [
  { value: "python", label: "Python" },
  { value: "java", label: "Java" },
  { value: "javascript", label: "JavaScript" },
  { value: "c", label: "C" },
  { value: "cpp", label: "C++" },
  { value: "csharp", label: "C#" },
  { value: "ruby", label: "Ruby" },
  { value: "swift", label: "Swift" },
];

/* ── Difficulty / number pill pickers — reuse .inst-type-toggle/.inst-type-btn,
   already shipped for the assessment-type toggle, so no new visual language. ── */

export function DifficultyPicker({ value, onChange }) {
  return (
    <div className="inst-type-toggle">
      {DIFFICULTY_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`inst-type-btn${value === o.value ? " active" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function NumberPicker({ value, onChange, options }) {
  return (
    <div className="inst-type-toggle">
      {options.map((n) => (
        <button
          key={n}
          type="button"
          className={`inst-type-btn${value === n ? " active" : ""}`}
          onClick={() => onChange(n)}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

/* ── JD upload dropzone — same idle/loading/done/error flow and markup as
   InstitutionalDashboard.jsx's CreateCampaignModal, styled with .qbm-jd-*
   (verbatim values copied from .inst-jd-* in InstitutionalDashboard.css). ── */

const EMPTY_JD = { file: null, status: "idle", error: "" };

export function JdUpload({ text, onTextChange }) {
  const [jdUpload, setJdUpload] = useState(EMPTY_JD);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "docx"].includes(ext)) {
      setJdUpload({ file: null, status: "error", error: "Only PDF or DOCX files are accepted." });
      return;
    }
    setJdUpload({ file, status: "loading", error: "" });
    try {
      const data = await extractCompanyJd(file);
      onTextChange(data.text);
      setJdUpload({ file, status: "done", error: "" });
    } catch (ex) {
      setJdUpload({ file, status: "error", error: ex.message || "Could not read the file." });
    }
  };

  const clear = () => {
    setJdUpload(EMPTY_JD);
    onTextChange("");
  };

  return (
    <div className="qbm-jd">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx"
        style={{ display: "none" }}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {jdUpload.status === "idle" || jdUpload.status === "error" ? (
        <button type="button" className="qbm-jd-dropzone" onClick={() => inputRef.current?.click()}>
          <span className="qbm-jd-dropzone__icon">📄</span>
          <span className="qbm-jd-dropzone__text">Click to upload PDF or DOCX</span>
          <span className="qbm-jd-dropzone__hint">Max 10 MB — optional, tailors generated questions</span>
          {jdUpload.status === "error" && <span className="qbm-jd-dropzone__err">{jdUpload.error}</span>}
        </button>
      ) : jdUpload.status === "loading" ? (
        <div className="qbm-jd-status qbm-jd-status--loading">
          <span className="qbm-jd-spinner" />
          Extracting text from {jdUpload.file.name}…
        </div>
      ) : (
        <div className="qbm-jd-status qbm-jd-status--done">
          <span className="qbm-jd-done-icon">✓</span>
          <span className="qbm-jd-filename">{jdUpload.file.name}</span>
          <span className="qbm-jd-chars">{text.length} chars extracted</span>
          <button type="button" className="qbm-jd-clear" onClick={clear}>✕</button>
        </div>
      )}
    </div>
  );
}

/* ── Editable plain-text question list — used for AI Interview questions,
   whether hand-typed or AI-generated, so the result is always reviewable. ── */

export function EditableQuestionList({ questions, onChange }) {
  const [editingIdx, setEditingIdx] = useState(null);
  const [draft, setDraft] = useState("");

  const startEdit = (i) => {
    setEditingIdx(i);
    setDraft(questions[i]);
  };

  const commitEdit = () => {
    if (editingIdx === null) return;
    const text = draft.trim();
    if (text) {
      onChange(questions.map((q, i) => (i === editingIdx ? text : q)));
    }
    setEditingIdx(null);
  };

  const remove = (i) => onChange(questions.filter((_, idx) => idx !== i));
  const add = () => {
    onChange([...questions, ""]);
    setEditingIdx(questions.length);
    setDraft("");
  };

  return (
    <div className="qbm-list">
      {questions.length === 0 && <p className="qbm-empty">No questions yet.</p>}
      {questions.map((q, i) => (
        <div key={i} className="qbm-row">
          <div className="qbm-row-main">
            {editingIdx === i ? (
              <textarea
                className="qbm-eq-edit"
                value={draft}
                autoFocus
                rows={2}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commitEdit}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    commitEdit();
                  }
                }}
              />
            ) : (
              <p className="qbm-row-question qbm-eq-text" onClick={() => startEdit(i)}>{q || "(empty — click to edit)"}</p>
            )}
          </div>
          <div className="qbm-row-actions">
            {editingIdx !== i && (
              <button className="icon-button" type="button" title="Edit" onClick={() => startEdit(i)}>✎</button>
            )}
            <button className="icon-button" type="button" title="Remove" onClick={() => remove(i)}>🗑</button>
          </div>
        </div>
      ))}
      <button type="button" className="link-button" onClick={add}>+ Add question</button>
    </div>
  );
}

/* ── AI-generation panel — role, JD, difficulty, count (+ language for
   coding). Shared shape across MCQ / Coding / Interview so the "Generate
   with AI" step looks and behaves identically for all three. ── */

export function GenerateQuestionsPanel({
  eventType, role, onRoleChange, experience, onExperienceChange,
  jdText, onJdTextChange, difficulty, onDifficultyChange,
  numQuestions, onNumQuestionsChange, language, onLanguageChange,
  onGenerate, generating
}) {
  const isCoding = eventType === "CODING";
  const numOptions =
    eventType === "MCQ" ? [5, 10, 15, 20, 30, 40] : isCoding ? [1, 2, 3, 4, 5] : [5, 10, 15, 20];

  return (
    <div className="qbm-form qbm-gen-panel">
      <label>
        <span>Target Role</span>
        <input value={role} onChange={(e) => onRoleChange(e.target.value)} placeholder="e.g. Backend Engineer" />
      </label>
      <label>
        <span>Experience (optional)</span>
        <input value={experience} onChange={(e) => onExperienceChange(e.target.value)} placeholder="e.g. 3 years" />
      </label>
      <label>
        <span>Job Description<span className="qbm-gen-hint"> — optional, questions are drawn from it when provided</span></span>
        <JdUpload text={jdText} onTextChange={onJdTextChange} />
      </label>
      <label>
        <span>Difficulty</span>
        <DifficultyPicker value={difficulty} onChange={onDifficultyChange} />
      </label>
      <label>
        <span>Number of questions</span>
        <NumberPicker value={numQuestions} onChange={onNumQuestionsChange} options={numOptions} />
      </label>
      {isCoding && (
        <label>
          <span>Reference solution language</span>
          <select value={language} onChange={(e) => onLanguageChange(e.target.value)}>
            {CODING_LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </label>
      )}
      <div className="qbm-form-actions">
        <button
          type="button"
          className="primary-button"
          disabled={generating || !role.trim()}
          onClick={onGenerate}
        >
          {generating ? "Generating…" : "✨ Generate questions"}
        </button>
      </div>
    </div>
  );
}

/* ── Unified MCQ/Coding question source manager ────────────────────────────
   Same "Write manually | Upload Excel/CSV | Generate with AI" tab bar and
   always-editable question-card list for every place MCQ/Coding questions
   are authored — the Create Job form (local draft, saved on submit) and the
   post-creation "Manage Questions" screen (each action persists immediately).
   The component itself holds no question data — the caller owns the list and
   supplies onAdd/onUpdate/onRemove/onBulkAdd, which may be a local array
   update (create-time) or a network call (manage-time). ── */

export function QuestionSourceManager({
  eventType, companyName, questions, onAdd, onUpdate, onRemove, onBulkAdd, allowUpload = true,
  initialRole = "", initialExperience = "", initialJdText = "", jobId = null
}) {
  const isMcq = eventType === "MCQ";
  const [tab, setTab] = useState("manual");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [confirmingUpload, setConfirmingUpload] = useState(false);
  // Pre-filled from the pipeline job's already-entered JD/criteria when this
  // is a pipeline assessment (AssessmentManagerModal.jsx) — blank ("") for
  // every other caller, so this is a pure no-op there.
  const [genRole, setGenRole] = useState(initialRole);
  const [genExperience, setGenExperience] = useState(initialExperience);
  const [genJdText, setGenJdText] = useState(initialJdText);
  const [genDifficulty, setGenDifficulty] = useState("moderate");
  const [genNumQuestions, setGenNumQuestions] = useState(isMcq ? 10 : 2);
  const [genLanguage, setGenLanguage] = useState("python");
  const [generating, setGenerating] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await parseQuestionsFile(isMcq ? "mcq" : "coding", file);
      setUploadPreview(result);
    } catch (err) {
      toast.error(err.message || "Unable to parse file");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const confirmUpload = async () => {
    if (!uploadPreview?.parsed?.length) return;
    setConfirmingUpload(true);
    try {
      await onBulkAdd(uploadPreview.parsed);
      toast.success(`${uploadPreview.parsed.length} question(s) added`);
      setUploadPreview(null);
    } catch (err) {
      toast.error(err.message || "Unable to save questions");
    } finally {
      setConfirmingUpload(false);
    }
  };

  const handleGenerate = async () => {
    if (!genRole.trim()) {
      toast.error("Target role is required to generate questions.");
      return;
    }
    setGenerating(true);
    try {
      const result = await generateCompanyQuestions({
        event_type: eventType, role: genRole, company_name: companyName, experience: genExperience,
        jd_text: genJdText, difficulty: genDifficulty, num_questions: genNumQuestions,
        ...(isMcq ? {} : { language: genLanguage })
      });
      if (!result.parsed?.length) {
        toast.error("No questions were generated — try again.");
        return;
      }
      await onBulkAdd(result.parsed);
      if (result.error_count) {
        toast.error(`${result.error_count} generated question(s) failed validation and were skipped.`);
      }
      toast.success(`${result.parsed.length} question(s) added — review below`);
    } catch (err) {
      toast.error(err.message || "Unable to generate questions");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="qbm-source">
      <div className="qbm-tabs">
        <button type="button" className={`qbm-tab${tab === "manual" ? " active" : ""}`} onClick={() => setTab("manual")}>
          Write manually
        </button>
        {allowUpload && (
          <button type="button" className={`qbm-tab${tab === "upload" ? " active" : ""}`} onClick={() => setTab("upload")}>
            Upload Excel/CSV
          </button>
        )}
        <button type="button" className={`qbm-tab${tab === "ai" ? " active" : ""}`} onClick={() => setTab("ai")}>
          ✨ Generate with AI
        </button>
      </div>

      {tab === "manual" && (
        showAddForm ? (
          isMcq ? (
            <McqQuestionForm
              onSave={async (q) => { await onAdd(q); setShowAddForm(false); }}
              onCancel={() => setShowAddForm(false)}
            />
          ) : (
            <CodingQuestionForm
              onSave={async (q) => { await onAdd(q); setShowAddForm(false); }}
              onCancel={() => setShowAddForm(false)}
            />
          )
        ) : (
          <button type="button" className="secondary-button" onClick={() => setShowAddForm(true)}>
            + Add question manually
          </button>
        )
      )}

      {tab === "upload" && allowUpload && (
        <div className="qbm-form">
          <div className="qbm-toolbar">
            <button type="button" className="link-button" onClick={() => downloadTemplate(isMcq)}>
              ⭳ Download {isMcq ? "MCQ" : "coding"} template
            </button>
            <label className={`secondary-button qbm-upload-btn${uploading ? " disabled" : ""}`}>
              {uploading ? "Parsing…" : "Upload Excel / CSV"}
              <input type="file" accept=".xlsx,.xlsm,.csv" hidden onChange={handleFileChange} disabled={uploading} />
            </label>
          </div>
          {uploadPreview && (
            <UploadPreview
              preview={uploadPreview}
              isMcq={isMcq}
              saving={confirmingUpload}
              onConfirm={confirmUpload}
              onCancel={() => setUploadPreview(null)}
            />
          )}
        </div>
      )}

      {tab === "ai" && (
        <GenerateQuestionsPanel
          eventType={eventType}
          role={genRole} onRoleChange={setGenRole}
          experience={genExperience} onExperienceChange={setGenExperience}
          jdText={genJdText} onJdTextChange={setGenJdText}
          difficulty={genDifficulty} onDifficultyChange={setGenDifficulty}
          numQuestions={genNumQuestions} onNumQuestionsChange={setGenNumQuestions}
          language={genLanguage} onLanguageChange={setGenLanguage}
          onGenerate={handleGenerate}
          generating={generating}
        />
      )}

      <div className="qbm-list">
        {questions.length === 0 ? (
          <p className="qbm-empty">No questions yet.</p>
        ) : (
          questions.map((q, i) =>
            editingIdx === i ? (
              isMcq ? (
                <McqQuestionForm
                  key={q.question_id || i}
                  initial={q}
                  onSave={async (updated) => { await onUpdate(i, q, updated); setEditingIdx(null); }}
                  onCancel={() => setEditingIdx(null)}
                />
              ) : (
                <CodingQuestionForm
                  key={q.question_id || i}
                  jobId={jobId}
                  initial={q}
                  onSave={async (updated) => {
                    await onUpdate(i, q, { ...updated, verified: q.verified, verification_note: q.verification_note });
                    setEditingIdx(null);
                  }}
                  onCancel={() => setEditingIdx(null)}
                />
              )
            ) : (
              <QuestionRow
                key={q.question_id || i}
                question={q}
                isMcq={isMcq}
                onEdit={() => { setEditingIdx(i); setShowAddForm(false); }}
                onDelete={() => onRemove(i, q)}
              />
            )
          )
        )}
      </div>
    </div>
  );
}
